import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';
import { apiSlice } from './apiSlice';
import { TourClassTime, TourClassTimeDTO } from '../../@types/tourClassTime';

const tourClassTimeAdapter = createEntityAdapter<TourClassTime>({
  sortComparer: (a, b) => a.tourClass - b.tourClass,
});

const initialState = tourClassTimeAdapter.getInitialState();

export const tourClassTimeApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTourClassTimes: builder.query<EntityState<TourClassTime, number>, void>({
      query: () => '/tour-class-times',
      transformResponse: (response: TourClassTime[]): EntityState<TourClassTime, number> => {
        return tourClassTimeAdapter.setAll(initialState, response) as EntityState<
          TourClassTime,
          number
        >;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.ids.map((id) => ({ type: 'TourClassTime' as const, id })),
              { type: 'TourClassTime', id: 'LIST' },
            ]
          : [{ type: 'TourClassTime', id: 'LIST' }],
    }),

    getTourClassTimeById: builder.query<TourClassTime, number>({
      query: (id) => `/tour-class-times/${id}`,
      providesTags: (result, error, id) => [{ type: 'TourClassTime', id }],
    }),

    getTourClassTimesByTourBundle: builder.query<EntityState<TourClassTime, number>, number>({
      query: (tourBundleId) => `/tour-class-times/by-tour-bundle/${tourBundleId}`,
      transformResponse: (response: TourClassTime[]): EntityState<TourClassTime, number> => {
        return tourClassTimeAdapter.setAll(initialState, response) as EntityState<
          TourClassTime,
          number
        >;
      },
      providesTags: (result, error, tourBundleId) => [
        { type: 'TourClassTime', id: `BUNDLE_${tourBundleId}` },
      ],
    }),

    getDefaultTourClassTimes: builder.query<EntityState<TourClassTime, number>, void>({
      query: () => '/tour-class-times/default',
      transformResponse: (response: TourClassTime[]): EntityState<TourClassTime, number> => {
        return tourClassTimeAdapter.setAll(initialState, response) as EntityState<
          TourClassTime,
          number
        >;
      },
      providesTags: [{ type: 'TourClassTime', id: 'DEFAULT' }],
    }),

    getTourClassByDuration: builder.query<TourClassTime, number>({
      query: (durationInMinutes) => ({
        url: '/tour-class-times/by-duration',
        params: { durationInMinutes },
      }),
      providesTags: [{ type: 'TourClassTime', id: 'BY_DURATION' }],
    }),
    batchCreateTourClassTimes: builder.mutation<TourClassTime[], TourClassTimeDTO[]>({
      query: (tourClassTimes) => ({
        url: '/tour-class-times/batch',
        method: 'POST',
        body: tourClassTimes,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'TourClassTime', id: 'LIST' },
        { type: 'TourClassTime', id: 'DEFAULT' },
        ...(arg.length > 0 && arg[0].tourBundleId
          ? [{ type: 'TourClassTime' as const, id: `BUNDLE_${arg[0].tourBundleId}` }]
          : []),
      ],
    }),
    deleteTourClassTime: builder.mutation<void, number>({
      query: (id) => ({
        url: `/tour-class-times/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'TourClassTime', id },
        { type: 'TourClassTime', id: 'LIST' },
        { type: 'TourClassTime', id: 'DEFAULT' },
        { type: 'TourBundle', id: 'LIST' },
      ],
    }),
    batchUpdateTourClassTimes: builder.mutation<TourClassTime[], TourClassTime[]>({
      query: (tourClassTimes) => ({
        url: '/tour-class-times/batch',
        method: 'PUT',
        body: tourClassTimes,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'TourClassTime', id: 'LIST' },
        ...(arg.length > 0
          ? [{ type: 'TourClassTime' as const, id: `BUNDLE_${arg[0].tourBundleId}` }]
          : []),
      ],
    }),
  }),
});

export const {
  useGetTourClassTimesByTourBundleQuery,
  useBatchUpdateTourClassTimesMutation,
  useBatchCreateTourClassTimesMutation,
} = tourClassTimeApiSlice;
